import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true, // Enable sending cookies with requests
  validateStatus: function (status) {
    // Consider any status less than 500 as a success
    // This prevents axios from throwing errors on 4xx responses
    return status < 500;
  }
});

// Add request interceptor to add Authorization header from cookie if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('AUTH-TOKEN');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth responses and errors
apiClient.interceptors.response.use(
  (response) => {
    // Handle successful responses
    const authHeader = response.headers['authorization'];
    if (authHeader && authHeader.startsWith('Bearer ')) {
      localStorage.setItem('AUTH-TOKEN', authHeader.substring(7));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle auth errors (401/403)
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Clear auth state
      localStorage.removeItem('AUTH-TOKEN');
      localStorage.removeItem('username');

      // If this is the first retry attempt for a 401
      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Notify about session expiration
        const authError = new CustomEvent('auth-error', {
          detail: {
            message: 'Your session has expired. Please log in again.',
            status: error.response.status
          }
        });
        window.dispatchEvent(authError);
      }

      return Promise.reject(new Error('Authentication failed'));
    }

    // Handle network errors
    if (!error.response) {
      return Promise.reject(new Error('Network error occurred'));
    }

    return Promise.reject(error);
  }
);

// Add request interceptor to handle request retries
apiClient.interceptors.request.use(
  async (config) => {
    // If this is a retry, wait a bit before sending the request
    if (config._retry) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const getModelInfo = () => {
  return apiClient.get('/api/system/model-info');
};

export const getVersion = () => {
  return apiClient.get('/api/system/version');
};

export default apiClient;
